/**
 * The optimistic stage overlay, and which write owns each card's entry.
 *
 * A move shows on the board before the API has agreed to it, and is put back
 * if the API refuses. Doing that with the stage alone has a race: two moves on
 * one card can overlap, and a first request failing after a second has started
 * would roll the card back to where it was before the *first* move, throwing
 * away a newer stage the person can see on screen and believes is saved.
 *
 * So every move carries a token, and a card's entry records the token that
 * wrote it. A failure may only undo its own work.
 */
export type StageOverrides = {
  /** cardId -> the stage being shown ahead of the API. */
  stages: Record<string, string>
  /** cardId -> the token of the write that put it there. */
  owner: Record<string, number>
}

export const NO_OVERRIDES: StageOverrides = { stages: {}, owner: {} }

/** Shows `stage` on `cardId` straight away, marking this write as its owner. */
export function beginMove(state: StageOverrides, cardId: string, stage: string, token: number): StageOverrides {
  return {
    stages: { ...state.stages, [cardId]: stage },
    owner: { ...state.owner, [cardId]: token },
  }
}

/**
 * Puts `cardId` back to `previous` after a refused write.
 *
 * A no-op when a later move has taken the card over: that move is showing its
 * own stage and owns the outcome, including its own failure. Rolling back on
 * top of it would replace a newer stage with an older one and leave the board
 * disagreeing with both the API and the person looking at it.
 */
export function failMove(
  state: StageOverrides,
  cardId: string,
  previous: string | undefined,
  token: number,
): StageOverrides {
  if (state.owner[cardId] !== token) return state
  const stages = { ...state.stages }
  const owner = { ...state.owner }
  delete owner[cardId]
  if (previous === undefined) delete stages[cardId]
  else stages[cardId] = previous
  return { stages, owner }
}

/** Whether this write still owns the card, i.e. nothing newer has superseded it. */
export function ownsMove(state: StageOverrides, cardId: string, token: number): boolean {
  return state.owner[cardId] === token
}
